import { z } from 'zod';

/** @see AppleRetail.Web.Areas.StoreEvents.Models.ShipmentReportItemModel */
export interface ShipmentReportItemModel {
	partNumber: string;
	name: string;
	images: ShipmentReportImage[];
	unit: string;
	comment: string | null;
	quantityOrdered: number;
	quantityShipped: number;
	carriers: string | null;
	trackingNumbers: string | null;
}

/** @see Manzanita.Catalog.ManzanitaSortedImage */
export interface ShipmentReportImage {
	sortOrder: number;
	largeImageUrl: string;
	smallImageUrl: string;
}

/** @see AppleRetail.Web.Areas.StoreEvents.Models.ShipmentReportCategoryModel */
export interface ShipmentReportCategoryModel {
	name: string;
	labelImageUrl: string | null;
	items: ShipmentReportItemModel[];
}

/** @see AppleRetail.Web.Areas.StoreEvents.Models.ShipmentReportModel */
export interface ShipmentReportModel {
	eventType: string;
	addressLineOne: string;
	addressLineTwo: string | null;
	city: string;
	state: string;
	postalCode: string;
	countryCode: string;
	percentShipped: number;
	openingDate: string | null;
	fullKitPickDate: string | null;
	fullKitShipDate: string | null;
	fullKitDeliveryDate: string | null;
	earlyManagementKitPickDate: string | null;
	earlyManagementKitShipDate: string | null;
	earlyManagementKitDeliveryDate: string | null;
	generatedDate: string | null;
	categories: ShipmentReportCategoryModel[];
	defaultImage: ShipmentReportImage;
	storeName: string;
	storeCode: string;
}

const shipmentReportImageSchema = z.object({
	sortOrder: z.number(),
	largeImageUrl: z.string(),
	smallImageUrl: z.string(),
});

const shipmentReportItemModelSchema = z.object({
	partNumber: z.string(),
	name: z.string(),
	images: z.array(shipmentReportImageSchema),
	unit: z.string(),
	comment: z.string().nullable(),
	quantityOrdered: z.number(),
	quantityShipped: z.number(),
	carriers: z.string().nullable(),
	trackingNumbers: z.string().nullable(),
});

const shipmentReportCategoryModelSchema = z.object({
	name: z.string(),
	labelImageUrl: z.string().nullable(),
	items: z.array(shipmentReportItemModelSchema),
});

/** @see AppleRetail.Web.Areas.StoreEvents.Models.ShipmentReportModel */
export const shipmentReportModelSchema = z.object({
	eventType: z.string(),
	addressLineOne: z.string(),
	addressLineTwo: z.string().nullable(),
	city: z.string(),
	state: z.string(),
	postalCode: z.string(),
	countryCode: z.string(),
	percentShipped: z.number(),
	openingDate: z.string().nullable(),
	fullKitPickDate: z.string().nullable(),
	fullKitShipDate: z.string().nullable(),
	fullKitDeliveryDate: z.string().nullable(),
	earlyManagementKitPickDate: z.string().nullable(),
	earlyManagementKitShipDate: z.string().nullable(),
	earlyManagementKitDeliveryDate: z.string().nullable(),
	generatedDate: z.string().nullable(),
	categories: z.array(shipmentReportCategoryModelSchema),
	defaultImage: shipmentReportImageSchema,
	storeName: z.string(),
	storeCode: z.string(),
}) as z.ZodType<ShipmentReportModel>;
